import React, { JSX, useContext, useEffect, useState } from "react";
import { EntityTypeEnum } from "@app/projects/enumerators";
import { ContractEntityDetailModel, ContractEntityHeaderInfo, ContractEntityScores, TinInformationModel } from "@app/projects/servicing-provider/providerAffiliation";
import SpecialtyBreakdownAccordion from "@app/projects/servicing-provider/provider-affiliations/SpecialtyBreakdownAccordion";
import FacilitiesAccordion from "@app/projects/servicing-provider/provider-affiliations/FacilitiesAccordion";
import ProviderAffiliationService from "@app/projects/providerAffiliationsService";
import PerformanceOverviewAccordion from "@app/projects/servicing-provider/provider-affiliations/PerformanceOverviewAccordion";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import OrganizationAccordion from "./OrganizationAccordion";
import TinInformationAccordion from "@app/projects/servicing-provider/provider-affiliations/TinInformationAccordion";
import Features from "@app/feature-flags/features";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { SpecialtyCountModel } from "@app/projects/network-assessment/provider-hub/interfaces";

interface ContractEntityProfileProps {
  showPerformanceOverview?: boolean;
  contractEntityId: number;
  contractEntityName: string;
  providerEntityType: string;
  versionId: number;
}

const ContractEntityProfile = (props: ContractEntityProfileProps): JSX.Element => {
  const [ceDetailData, setCeDetailData] = useState<ContractEntityDetailModel>();
  const [contractEntityHeaderInfo, setContractEntityHeaderInfo] = useState<ContractEntityHeaderInfo>();
  const [scores, setScores] = useState<ContractEntityScores>();
  const [tinInfo, setTinInfo] = useState<TinInformationModel[]>();
  const tinInformationFeatureFlagEnabled = React.useContext(FeatureFlagContext).enabledFeatureFlags.has(Features.PROVIDER_AFFILIATIONS_TIN_INFORMATION);
  const projectContext = useContext(CountyProjectContext);
  const [providerHubSpecialties, setProviderHubSpecialties] = useState<SpecialtyCountModel[]>();
  const [specialtyAccordionTitle, setSpecialtyAccordionTitle] = useState<string>("Top 5 Specialties");
  
  useEffect(() => {
    const providerAffiliationService = new ProviderAffiliationService();
    if(projectContext?.project?.projectId != null && projectContext?.runConfiguration?.isServicingProviderInSnowflake){
      providerAffiliationService.GetSpecialtiesByContractEntityId(props.contractEntityId, projectContext.project.projectId, projectContext?.runConfiguration?.projectRunConfigurationId)
      .then((data) => 
        setProviderHubSpecialties(data));
        setSpecialtyAccordionTitle("Specialties");
    }
      providerAffiliationService.GetContractEntityDetailsById(props.contractEntityId, props.versionId).then((data) => {
        setCeDetailData(data);
      });

    providerAffiliationService.getContractEntityHeaderFieldsById(props.versionId, props.contractEntityId).then((data) => {
      data.contactNumber = formatPhoneNumber(data.contactNumber);
      setContractEntityHeaderInfo(data);
    });
    
    if(projectContext?.project?.projectId != null){
      providerAffiliationService.GetContractEntityScores(props.contractEntityId, props.versionId, projectContext.project.projectId, 
      projectContext?.runConfiguration?.projectRunConfigurationId).then((data) => {
      setScores(data);
    })
    } else {
      providerAffiliationService.GetHDAContractEntityScores(props.contractEntityId, props.versionId).then((data) => {
      setScores(data);
    })
    }
    
    providerAffiliationService.getTinInformation(props.contractEntityId, props.versionId).then((data) => {
      setTinInfo(data);
    })
  }, []);

  const formatPhoneNumber = (phoneNumber: string): string => {
    const cleanedPhoneNumber = phoneNumber.replace(/\D/g, "");
    let phoneNumberWithDashes = "";

    if (cleanedPhoneNumber.length === 12) {
      phoneNumberWithDashes = "+"+cleanedPhoneNumber.slice(0,2)+" ("+cleanedPhoneNumber.slice(2,5)+") "+cleanedPhoneNumber.slice(5,8)+"-"+cleanedPhoneNumber.slice(8);
      return phoneNumberWithDashes;
    }
    else if (cleanedPhoneNumber.length === 11) {
      phoneNumberWithDashes = "+"+cleanedPhoneNumber.slice(0,1)+" ("+cleanedPhoneNumber.slice(1,4)+") "+cleanedPhoneNumber.slice(4,7)+"-"+cleanedPhoneNumber.slice(7);
      return phoneNumberWithDashes;
    }
    else if (cleanedPhoneNumber.length === 10) {
      phoneNumberWithDashes = "("+cleanedPhoneNumber.slice(0,3)+") "+cleanedPhoneNumber.slice(3,6)+"-"+cleanedPhoneNumber.slice(6);
      return phoneNumberWithDashes;
    }

    return phoneNumber;
  }

  const getEntityIconClasses = (entityType: string, additionalClasses?: string): string => {
    let iconClasses: string;
    switch (entityType) {
      case EntityTypeEnum.Individual:
        iconClasses = "fa fa-user-md";
        break;
      case EntityTypeEnum.Facility:
      default:
        iconClasses = "fa fa-building";
        break;
    }

    if (additionalClasses) {
      iconClasses.concat(additionalClasses);
    }

    return iconClasses;
  };


  return (
    <div className="servicing-provider-profile">
      {ceDetailData && (
        <>
          <section className="intro">
            <div className="row">
              <div className="col-sm-3 mb-3">
                <i className={getEntityIconClasses(props.providerEntityType)} />
              </div>

              <div className="col-sm-9 mb-3">
                <h1 className="pl0-imp">
                  {props.contractEntityName}
                </h1>
                {contractEntityHeaderInfo.entityType ?
                  <div>
                    <strong>Entity Type: </strong> {contractEntityHeaderInfo.entityType}
                    <br />
                  </div> :
                  null
                }
                {contractEntityHeaderInfo.entitySize ?
                  <div>
                    <strong>Entity Size: </strong> {contractEntityHeaderInfo.entitySize}
                    <br />
                  </div> :
                  null
                }
                <div>
                  <strong>Total Number of Facilities: </strong> {ceDetailData.facilities.length}
                </div>
                {contractEntityHeaderInfo.contactNumber ?
                  <div>
                    <strong>Contact Number: </strong> {contractEntityHeaderInfo.contactNumber}
                    <br />
                  </div> :
                  null
                }
              </div>
            </div>
          </section>

          <AccordionContainer>
            { props.showPerformanceOverview ? (
              <Accordion title="Performance Overview" testId="contract-entity-profile__performance-overview-accordion">
                <PerformanceOverviewAccordion
                  effectivenessScore={scores?.effectivenessScore ?? 0}
                  efficiencyScore={scores?.efficiencyScore ?? 0}
                />
              </Accordion>
              ) : (
              <Accordion title="Performance Overview" testId="contract-entity-profile__performance-overview-accordion">
                <p>Provider Claims Insight (PCI) License Required to View this Information</p>
              </Accordion>
              )
            }
            <Accordion title={specialtyAccordionTitle} testId="contract-entity-profile__specialty-accordion">
              <SpecialtyBreakdownAccordion
                specialties={ceDetailData.specialties ?? []}
                providerHubSpecialties={providerHubSpecialties}
              />
            </Accordion>
            {tinInformationFeatureFlagEnabled && tinInfo && (
              <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
                <Accordion title="TINs" testId="contract-entity-profile__tin-information">
                  <TinInformationAccordion
                    tins={tinInfo ?? []}
                  >
                  </TinInformationAccordion>
                </Accordion>
              </RoleRestricted>
            )}             
            <Accordion title="Organizations" testId="contract-entity-profile__organizations-accordion">
              <OrganizationAccordion
                organizations={ceDetailData.organizations ?? []}
              />
            </Accordion>
            <Accordion title="Facilities" testId="contract-entity-profile__facilities-accordion">
              <FacilitiesAccordion
                facilities={ceDetailData.facilities ?? []}
              />
            </Accordion>
          </AccordionContainer>

        </>
       )} 
    </div>
  )
}

export default ContractEntityProfile;
